import type { TourStep } from '@/components/ui/Tour';
import type { PageTourKey } from './registry';
import {
  type BookingsListTourContext,
  getBookingsListTourSteps,
} from './bookings-management-tour-steps';
import { schedulePageTourStart } from './scheduleAutoPageTour';

export type BookingsManagementTab = 'bookings' | 'waitlist' | 'eoi' | 'private-requests';

export type { BookingsListTourContext };

export const BOOKINGS_LIST_TOUR_KEY: PageTourKey = 'academy-bookings-list';

export interface BookingsTabHelpContent {
  title: string;
  description: string;
  tourLabel?: string;
  tourDescription?: string;
}

export const BOOKINGS_TAB_HELP: Record<BookingsManagementTab, BookingsTabHelpContent> = {
  bookings: {
    title: 'Bookings',
    description:
      'Confirm enrollments, review payment status, and use bulk actions to confirm or cancel pending bookings.',
    tourLabel: 'Tour this tab',
    tourDescription: 'Filter, confirm, and bulk-manage lesson and camp enrollments.',
  },
  waitlist: {
    title: 'Waitlist',
    description:
      'When lessons are full, guardians can join the waitlist. Review queue position and remove entries when needed.',
  },
  eoi: {
    title: 'Expressions of interest',
    description:
      'Review families interested in invitation-only programs, then send invites or decline submissions.',
  },
  'private-requests': {
    title: 'Private requests',
    description:
      'Approve or reject guardian-initiated private lesson packages from your public site. Use Calendar for staff-scheduled private packages.',
  },
};

export function getBookingsTabHelp(tab: BookingsManagementTab): BookingsTabHelpContent {
  return BOOKINGS_TAB_HELP[tab];
}

export function getBookingsTabForTourKey(tourKey: PageTourKey): BookingsManagementTab | null {
  return tourKey === BOOKINGS_LIST_TOUR_KEY ? 'bookings' : null;
}

export function resolveBookingsTabTourKey(
  tab: BookingsManagementTab,
): PageTourKey | null {
  return tab === 'bookings' ? BOOKINGS_LIST_TOUR_KEY : null;
}

export function scheduleBookingsListTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  ctx: BookingsListTourContext,
): () => void {
  return schedulePageTourStart(
    () =>
      startTourWithCompletionKey('academy-bookings-list', getBookingsListTourSteps(ctx)),
    'academy-bookings-list',
  );
}
